import { useCallback, useMemo } from 'react'
import { usePostHogFeatureFlag } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { POSTHOG_FEATURE_FLAGS } from '@percent/cause-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'
import { useMetaOfferAllowedInCountry } from '@percent/cause-dashboard/common/hooks/useMetaOfferAllowedInCountry/useMetaOfferAllowedInCountry'
import {
  SMART_APPLY_PARTNER_CONFIG,
  type SmartApplyPartnerEntry,
  type SmartApplyPartner
} from '@percent/cause-dashboard/constants/smartApplyPartnerConfig'

const ALL_PARTNERS: SmartApplyPartner[] = ['adobe', 'adobeExpress', 'openai', 'zoom', 'claude', 'meta', 'hootsuite']

export type UseEnabledSmartApplyPartnersResult = {
  enabledPartners: SmartApplyPartner[]
  isPartnerEnabled: (partner: SmartApplyPartner) => boolean
  getConfig: (partner: SmartApplyPartner) => SmartApplyPartnerEntry
}

export function useEnabledSmartApplyPartners(): UseEnabledSmartApplyPartnersResult {
  const isMetaEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_META_SMART_APPLY)
  const isAdobeExpressEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_ADOBE_EXPRESS_SECOND_SMART_APPLY)
  const isMetaAllowedInCountry = useMetaOfferAllowedInCountry(isMetaEnabled)

  const flagResults: Partial<Record<SmartApplyPartner, boolean>> = useMemo(
    () => ({
      adobeExpress: isAdobeExpressEnabled,
      meta: isMetaEnabled && isMetaAllowedInCountry
    }),
    [isAdobeExpressEnabled, isMetaEnabled, isMetaAllowedInCountry]
  )

  const enabledPartners = useMemo(() => ALL_PARTNERS.filter(partner => flagResults[partner] ?? true), [flagResults])

  const isPartnerEnabled = useCallback(
    (partner: SmartApplyPartner): boolean => flagResults[partner] ?? true,
    [flagResults]
  )

  const getConfig = useCallback(
    (partner: SmartApplyPartner): SmartApplyPartnerEntry => SMART_APPLY_PARTNER_CONFIG[partner],
    []
  )

  return { enabledPartners, isPartnerEnabled, getConfig }
}
